import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HttpEventType } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';
import { AdminFeaturesService } from '../service/admin-features.service';

interface UploadTask {
  id: number;
  name: string;
  progress: number;           // 0-100
  status: 'uploading' | 'done' | 'error';
}

@Component({
  selector: 'app-media-vault-admin',
  templateUrl: './media-vault-admin.component.html',
  providers: [MessageService],
})
export class MediaVaultAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  // Backend origin (strip trailing /api/) so stored /upload/... paths resolve.
  origin = environment.baseUrl.replace(/\/api\/?$/, '');

  albums: any[] = [];        // one per EVENT (empty ones included)
  years: string[] = [];
  filterYear = '';
  search = '';
  private searchTimer: any = null;
  loading = false;

  // drill-down: event -> sport -> items
  event: any = null;         // { eventId, eventName, eventYear, startDate, endDate }
  games: any[] = [];         // sports inside the open event
  game: any = null;          // selected sport
  items: any[] = [];

  // add-album modal
  addModal = false;
  addOptions: any[] = [];
  addLoading = false;
  addEventId: number | null = null;
  addSearch = '';
  private addTimer: any = null;
  creating = false;

  // upload modal
  uploadModal = false;
  files: File[] = [];
  up: any = { gameId: null };

  // background upload status panel (Google Drive-style, bottom-right)
  uploadQueue: UploadTask[] = [];
  uploadPanelOpen = true;
  uploadPanelVisible = false;
  private uploadSeq = 0;

  constructor(private svc: AdminFeaturesService, private toast: MessageService) {}

  ngOnInit() {
    this.svc.mvYears().pipe(takeUntil(this.destroy$)).subscribe((y: string[]) => this.years = y || []);
    this.loadAlbums();
  }

  onYearChange() { this.loadAlbums(); }
  /** Typing filters server-side; debounced so a long event name is not one request per key. */
  onSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadAlbums(), 300);
  }
  clearFilters() { this.filterYear = ''; this.search = ''; this.loadAlbums(); }

  src(url: string) { return url && url.indexOf('http') === 0 ? url : this.origin + url; }

  /**
   * Path for `appAuthImg`, relative to the upload root.
   *
   * /upload is behind the auth gate - `media-vault/` is in AUTHENTICATED_PREFIXES, so
   * it needs a logged-in user. A plain `src=` is a browser navigation and sends no
   * Authorization header, so every thumbnail came back 401 and rendered broken. The
   * directive refetches through HttpClient (interceptor attaches the token) and shows
   * the blob instead.
   */
  vaultPath(url: string): string {
    return (url || '').replace(/^\/?upload\//, '');
  }
  isExternal(url: string): boolean { return !!url && url.indexOf('http') === 0; }

  loadAlbums() {
    this.loading = true;
    this.svc.mvAlbums(this.filterYear, this.search).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any[]) => { this.albums = r || []; this.loading = false; },
      () => { this.loading = false; this.err('Failed to load albums'); });
  }

  /** Open one event: load its sports, then show the first sport that has photos. */
  openEvent(a: any) {
    this.svc.mvEventGames(a.eventId).pipe(takeUntil(this.destroy$)).subscribe((r: any) => {
      this.event = r.event;
      this.games = r.games || [];
      const withMedia = this.games.filter(g => g.count > 0);
      this.selectGame(withMedia.length ? withMedia[0] : (this.games[0] || null));
    }, () => this.err('Failed to open event'));
  }

  selectGame(g: any) {
    this.game = g;
    if (!g) { this.items = []; return; }
    this.svc.mvItems(this.event.eventId, g.gameId).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any[]) => this.items = r || [], () => this.err('Failed to load items'));
  }

  /** Refresh sport counts without losing the selected sport. */
  private refreshEvent() {
    const keep = this.game ? this.game.gameId : null;
    this.svc.mvEventGames(this.event.eventId).pipe(takeUntil(this.destroy$)).subscribe((r: any) => {
      this.games = r.games || [];
      const same = this.games.find(g => g.gameId === keep);
      this.selectGame(same || this.games[0] || null);
    });
  }

  backToAlbums() { this.event = null; this.game = null; this.games = []; this.items = []; this.loadAlbums(); }

  // ── Add album ────────────────────────────────────────────────────────────────
  // An album is created deliberately for one event; it is not implied by an event
  // existing, nor by a photo being uploaded. The picker only offers events that do
  // not already have one, so the unique constraint can never be hit by hand.
  openAdd() {
    this.addModal = true;
    this.addEventId = null;
    this.addSearch = '';
    this.loadAddOptions();
  }
  closeAdd() { this.addModal = false; }

  loadAddOptions() {
    this.addLoading = true;
    this.svc.mvAvailableEvents(this.filterYear, this.addSearch).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any[]) => { this.addOptions = r || []; this.addLoading = false; },
      () => { this.addLoading = false; this.err('Failed to load events'); });
  }
  onAddSearch() {
    if (this.addTimer) clearTimeout(this.addTimer);
    this.addTimer = setTimeout(() => this.loadAddOptions(), 300);
  }

  createAlbum() {
    if (!this.addEventId) { this.err('Select an event'); return; }
    this.creating = true;
    this.svc.mvCreateAlbum(this.addEventId).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.creating = false;
        this.closeAdd();
        this.ok('Album created');
        this.loadAlbums();
      },
      () => { this.creating = false; this.err('Could not create the album'); });
  }

  /** Removes the album itself, with every photo in it. */
  deleteAlbumRecord(a: any, ev: Event) {
    ev.stopPropagation();
    const msg = a.count
      ? `Delete the "${a.eventName}" album and all ${a.count} photo(s)? This cannot be undone.`
      : `Delete the empty "${a.eventName}" album?`;
    if (!confirm(msg)) return;
    this.svc.mvDeleteAlbumRecord(a.albumId).pipe(takeUntil(this.destroy$)).subscribe(
      () => { this.ok('Album deleted'); this.loadAlbums(); },
      () => this.err('Failed to delete album'));
  }

  deletingAlbum = false;
  /** Deletes the photos of the SELECTED SPORT only - never the whole event. */
  deleteAlbum() {
    if (!this.event || !this.game) return;
    const label = `${this.event.eventName} - ${this.game.gameName}`;
    if (!confirm(`Delete the entire "${label}" album? This permanently removes all ${this.items.length} item(s). This cannot be undone.`)) return;
    this.deletingAlbum = true;
    this.svc.mvDeleteAlbum(this.event.eventId, this.game.gameId).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.deletingAlbum = false;
      this.ok('Album deleted');
      this.refreshEvent();
    }, () => { this.deletingAlbum = false; this.err('Failed to delete album'); });
  }

  // ── Upload ───────────────────────────────────────────────────────────────────
  // The event is always the one already open, so the modal only asks which sport.
  openUpload() {
    this.files = [];
    this.up = { gameId: this.game ? this.game.gameId : null };
    this.uploadModal = true;
  }
  closeUpload() { this.uploadModal = false; }
  onFile(ev: any) { this.files = ev.target.files ? Array.from(ev.target.files) as File[] : []; }

  submitUpload() {
    if (!this.files.length) { this.err('Choose file(s) to upload'); return; }
    const eventId = this.event ? this.event.eventId : null;
    const gameId = this.up.gameId;
    this.closeUpload();
    // Each file uploads independently in the background panel - closing the modal
    // doesn't cancel anything, matches Drive's "queue and keep working" UX.
    this.files.forEach(f => this.queueUpload(f, eventId, gameId));
  }

  private queueUpload(file: File, eventId: number | null, gameId: number | null) {
    const task: UploadTask = {
      id: ++this.uploadSeq,
      name: file.name,
      progress: 0,
      status: 'uploading',
    };
    this.uploadQueue.unshift(task);
    this.uploadPanelVisible = true;
    this.uploadPanelOpen = true;

    const fd = new FormData();
    fd.append('file', file, file.name);
    if (eventId) fd.append('eventId', String(eventId));
    if (gameId)  fd.append('gameId', String(gameId));

    this.svc.mvUploadWithProgress(fd).pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event.type === HttpEventType.UploadProgress && event.total) {
        task.progress = Math.round((100 * event.loaded) / event.total);
      } else if (event.type === HttpEventType.Response) {
        task.status = 'done';
        task.progress = 100;
        if (this.event) this.refreshEvent(); else this.loadAlbums();
      }
    }, () => { task.status = 'error'; this.err(`Failed to upload ${task.name}`); });
  }

  get uploadingCount() { return this.uploadQueue.filter(t => t.status === 'uploading').length; }
  get anyUploading()   { return this.uploadingCount > 0; }
  toggleUploadPanel()  { this.uploadPanelOpen = !this.uploadPanelOpen; }
  closeUploadPanel()   { this.uploadPanelVisible = false; this.uploadQueue = []; }

  remove(item: any) {
    if (!confirm('Remove this media item?')) return;
    this.svc.mvDelete(item.id).pipe(takeUntil(this.destroy$)).subscribe(() => { this.ok('Removed'); this.refreshEvent(); }, () => this.err('Delete failed'));
  }

  private ok(m: string)  { this.toast.add({ severity: 'success', summary: m }); }
  private err(m: string) { this.toast.add({ severity: 'error', summary: m }); }

  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
}
